import './App.css';
import './assets/css/Navigation.scss';
import { ThemeProvider } from '@mui/material/styles';
import { ErrorProvider, useError } from './providers/ErrorProvider';
import { ErrorBoundary } from './components/error-boundary/ErrorBoundary.tsx';
import { ToastContainer } from './components/toast/ToastContainer.tsx';
import { HttpInterceptorService } from './services/HttpInterceptorService';
import { useEffect, Suspense } from 'react';
import { LicenseInfo } from '@mui/x-license';
import { VITE_MUI_X_PRO_KEY } from './utilities/env';
import { ErrorType } from './types/errors';
import { coiTheme, coiDarkTheme } from './utilities/theme';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { router } from './Router.tsx';
import { Box } from '@mui/material';
import dayjsUTC from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';

LicenseInfo.setLicenseKey(VITE_MUI_X_PRO_KEY || '');

dayjs.extend(dayjsUTC);
dayjs.extend(dayjsTimezone);

const THEMES = {
  light: coiTheme,
  dark: coiDarkTheme,
};

/**
 * Toast container component that integrates with the error context
 */
const GlobalToastContainer = () => {
  const { toasts, removeToast } = useError();

  return <ToastContainer notifications={toasts} onRemove={removeToast} />;
};

/**
 * Error handling setup component
 */
const ErrorHandlingSetup = ({ children }: { children: React.ReactNode }) => {
  const { addError } = useError();

  useEffect(() => {
    // Setup HTTP interceptors with error handling
    HttpInterceptorService.setupInterceptors(undefined, (processedError) => {
      addError(processedError);
    });

    // Setup global error handlers for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      addError({
        message: 'An unexpected error occurred. Please try again.',
        type: 'APPLICATION_ERROR' as ErrorType,
        originalError: event.reason,
      });
    };

    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      addError({
        message: 'An unexpected error occurred. Please try again.',
        type: 'APPLICATION_ERROR' as ErrorType,
        originalError: event.error,
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [addError]);

  return <>{children}</>;
};

export const App = () => {
  return (
    <ErrorProvider>
      <ErrorBoundary>
        <ThemeProvider theme={THEMES.light}>
          <ErrorHandlingSetup>
            <Suspense
              fallback={
                <Box textAlign="center" p={10}>
                  Loading...
                </Box>
              }
            >
              <RouterProvider router={createBrowserRouter(router)} />
            </Suspense>
            <GlobalToastContainer />
          </ErrorHandlingSetup>
        </ThemeProvider>
      </ErrorBoundary>
    </ErrorProvider>
  );
};

export default App;
